import React, { useState, useCallback, ChangeEvent, KeyboardEvent, WheelEvent } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';

export type CurrencyInputChangeEvent = { target: { value: number | null; name?: string } };

interface CurrencyInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value?: number | string;
  onChange?: (event: CurrencyInputChangeEvent) => void;
  allowNegative?: boolean;
  maxValue?: number;
  decimalPlaces?: number;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value = '',
  onChange,
  allowNegative = false,
  maxValue,
  decimalPlaces = 2,
  ...textFieldProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>(() => {
    if (value === '' || value === null || value === undefined) return '';
    return String(value);
  });

  // Format and validate input
  const formatCurrency = useCallback((input: string): string => {
    // Remove all non-numeric characters except decimal point and minus
    let cleaned = input.replace(/[^\d.-]/g, '');

    // Handle negative sign
    if (!allowNegative) {
      cleaned = cleaned.replace(/-/g, '');
    } else {
      // Allow only one minus sign at the beginning
      const hasNegative = cleaned.startsWith('-');
      cleaned = cleaned.replace(/-/g, '');
      if (hasNegative) cleaned = '-' + cleaned;
    }

    // Allow only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit decimal places
    if (parts.length === 2 && parts[1].length > decimalPlaces) {
      cleaned = parts[0] + '.' + parts[1].substring(0, decimalPlaces);
    }

    return cleaned;
  }, [allowNegative, decimalPlaces]);

  // Handle input change
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const name = textFieldProps.name;

    // Allow empty input
    if (rawValue === '' || rawValue === '-') {
      setDisplayValue(rawValue);
      if (onChange) onChange({ target: { value: null, name } });
      return;
    }

    const formatted = formatCurrency(rawValue);
    const numericValue = parseFloat(formatted);

    // Validate against max value
    if (maxValue !== undefined && numericValue > maxValue) {
      return; // Don't update if exceeds max
    }

    setDisplayValue(formatted);

    // Call onChange with synthetic event carrying numeric value
    if (onChange) {
      if (formatted === '' || formatted === '-' || isNaN(numericValue)) {
        onChange({ target: { value: null, name } });
      } else {
        onChange({ target: { value: numericValue, name } });
      }
    }
  }, [formatCurrency, maxValue, onChange, textFieldProps.name]);

  // Prevent arrow key changes
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
    }
  }, []);

  // Prevent mouse wheel changes
  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    (event.target as HTMLElement).blur(); // Remove focus to prevent scroll
  }, []);

  // Sync external value changes
  React.useEffect(() => {
    if (value !== undefined && value !== null && String(value) !== displayValue) {
      setDisplayValue(String(value));
    }
  }, [value]);

  return (
    <TextField
      {...textFieldProps}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      inputProps={{
        inputMode: 'decimal',
        pattern: allowNegative ? '-?[0-9]*[.,]?[0-9]*' : '[0-9]*[.,]?[0-9]*',
        ...textFieldProps.inputProps,
      }}
      InputProps={{
        ...textFieldProps.InputProps,
      }}
    />
  );
};

export default CurrencyInput;